import React, { useEffect, useState } from 'react';
import { Empty, Spin } from 'antd';
import { getApiBaseUrl } from '../../config/api';
import { menuIngestPageImagePath } from '../../services/menuIngestApi';

interface Props {
  ingestionId: string;
  pageId: string | null;
  alt: string;
}

/**
 * La foto de una página, al lado del renglón que se está revisando.
 *
 * Verificar ochenta precios sin la carta enfrente no es revisar, es confiar — y
 * confiar es exactamente lo que esta pantalla existe para no tener que hacer.
 *
 * Va por `fetch` y no por un `<img src>` a secas porque el endpoint exige token:
 * el navegador no manda el Authorization en la carga de una imagen. El blob se
 * revoca al cambiar de página para no ir dejando megas colgando en una sesión de
 * revisión larga.
 */
const MenuIngestPageImage: React.FC<Props> = ({ ingestionId, pageId, alt }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!pageId) {
      setUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(
          `${getApiBaseUrl()}${menuIngestPageImagePath(ingestionId, pageId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(String(response.status));

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ingestionId, pageId]);

  if (!pageId) {
    return <Empty description="Elige un renglón para ver su página" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <Spin />
      </div>
    );
  }

  if (failed || !url) {
    return <Empty description="No se pudo cargar la imagen" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <img
      src={url}
      alt={alt}
      style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 6 }}
    />
  );
};

export default MenuIngestPageImage;
